import { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './SystemTour.css';

export interface TourStep {
  title: string;
  description: string;
  features?: string[];
}

interface SystemTourProps {
  show: boolean;
  onHide: () => void;
  onComplete: () => void;
  storageKey: string;
  tourKey: string;
  steps: TourStep[];
  /**
   * true のとき「次回以降は表示しない」チェックを表示。
   * 完了／閉じる時にチェック済みなら storageKey を保存する。
   * false（既定）のときは完了時に常に保存（サンプル管理など既存挙動）。
   */
  showDontShowAgain?: boolean;
}

export default function SystemTour({
  show,
  onHide,
  onComplete,
  storageKey,
  tourKey,
  steps,
  showDontShowAgain = false,
}: SystemTourProps) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (!show) {
      setIndex(0);
      setDontShowAgain(false);
    }
  }, [show]);

  const step = steps[index];
  if (!step) return null;

  const isLast = index >= steps.length - 1;
  const checkId = `${tourKey.replace(/\./g, '-')}-dont-show`;

  const persistHide = () => {
    try {
      window.localStorage?.setItem(storageKey, 'true');
    } catch (err) {
      console.warn('Failed to save tour completion to localStorage:', err);
    }
  };

  const finish = (fromComplete: boolean) => {
    if (fromComplete) {
      if (!showDontShowAgain || dontShowAgain) persistHide();
      onComplete();
    } else if (showDontShowAgain && dontShowAgain) {
      persistHide();
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={() => finish(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-compass me-2" aria-hidden />
          {t(`${tourKey}.title`)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="system-tour-step">
          <h5 className="system-tour-step__title">{step.title}</h5>
          <p className="system-tour-step__desc">{step.description}</p>
          {step.features && step.features.length > 0 ? (
            <ul className="system-tour-step__features">
              {step.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
          ) : null}
        </div>

        {showDontShowAgain ? (
          <Form.Check
            id={checkId}
            type="checkbox"
            className="system-tour-dont-show"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            label={t(`${tourKey}.dont_show_again`)}
          />
        ) : null}

        <div className="system-tour-nav">
          <Button
            variant="outline-secondary"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0}
          >
            {t(`${tourKey}.prev`)}
          </Button>
          <div className="system-tour-dots" role="tablist" aria-label={t(`${tourKey}.title`)}>
            {steps.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                className={`system-tour-dot${i === index ? ' is-active' : ''}`}
                aria-selected={i === index}
                aria-label={`${i + 1} / ${steps.length}`}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
          <Button
            variant="primary"
            onClick={() => (isLast ? finish(true) : setIndex((i) => i + 1))}
          >
            {isLast ? t(`${tourKey}.finish`) : t(`${tourKey}.next`)}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
